import { AIChatBox, type Message } from "@/components/AIChatBox";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { buildAIContextSnapshot } from "@/lib/aiScopeContext";
import { Bot, ChevronDown, Sparkles, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type ModuleAIChatWidgetProps = {
  enabled: boolean;
};

function resolveAssistantTitle(screenLabel: string) {
  if (screenLabel.includes("APS Física")) return "Assistente da APS Física";
  if (screenLabel.includes("APS Digital")) return "Assistente da APS Digital";
  if (screenLabel.includes("Telemedicina")) return "Assistente da Telemedicina";
  if (screenLabel.includes("Insights")) return "Assistente de Insights";
  return "Assistente do NR1";
}

function buildInitialMessages(screenLabel: string): Message[] {
  const assistantTitle = resolveAssistantTitle(screenLabel);

  return [
    {
      role: "system",
      content: `Você é o widget contextual de IA do PJC e só pode responder usando o contexto autorizado do módulo atual (${screenLabel}).`,
    },
    {
      role: "assistant",
      content: `Olá. Sou o **${assistantTitle}**. Posso responder perguntas curtas sobre os dados visíveis nesta tela.`,
    },
  ];
}

export default function ModuleAIChatWidget({ enabled }: ModuleAIChatWidgetProps) {
  const [open, setOpen] = useState(false);
  const [workspaceHydrated, setWorkspaceHydrated] = useState(false);
  const contextSnapshot = useMemo(
    () => buildAIContextSnapshot(typeof window === "undefined" ? "/" : window.location.pathname),
    [],
  );
  const assistantTitle = useMemo(() => resolveAssistantTitle(contextSnapshot.screenLabel), [contextSnapshot.screenLabel]);
  const initialMessages = useMemo(() => buildInitialMessages(contextSnapshot.screenLabel), [contextSnapshot.screenLabel]);
  const [messages, setMessages] = useState<Message[]>(() => buildInitialMessages(contextSnapshot.screenLabel));

  const workspaceQuery = trpc.insights.getWorkspaceContext.useQuery(
    { surface: "widget", contextSnapshot },
    {
      enabled,
      staleTime: 60_000,
      retry: false,
    },
  );

  const chatMutation = trpc.insights.chat.useMutation({
    onSuccess: (result) => {
      setMessages((prev) => [...prev, { role: "assistant", content: result.reply }]);
    },
    onError: (error) => {
      const safeMessage = error.data?.code === "FORBIDDEN"
        ? `Eu não tenho permissão para responder isso neste contexto autenticado de ${assistantTitle}. Posso continuar apenas com a leitura do escopo disponível.`
        : `Não consegui concluir essa leitura com segurança agora. Tente novamente em instantes ou reformule a pergunta dentro do contexto visível de ${assistantTitle}.`;

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: safeMessage,
        },
      ]);
    },
  });

  useEffect(() => {
    if (!enabled) {
      setOpen(false);
      setMessages(initialMessages);
      setWorkspaceHydrated(false);
      return;
    }

    if (!workspaceQuery.data || workspaceHydrated) {
      return;
    }

    const scopeLabel = workspaceQuery.data.scope.hasCompanyContext
      ? workspaceQuery.data.scope.companyName || `Empresa ${workspaceQuery.data.scope.companyId}`
      : "a visão geral do módulo";

    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        content: `Leitura pronta para ${scopeLabel}. Pode me perguntar sobre indicadores, prioridades ou pontos de atenção desta tela.`,
      },
    ]);
    setWorkspaceHydrated(true);
  }, [enabled, initialMessages, workspaceHydrated, workspaceQuery.data]);

  const handleSendMessage = async (content: string) => {
    setMessages((prev) => [...prev, { role: "user", content }]);
    await chatMutation.mutateAsync({ message: content, surface: "widget", contextSnapshot });
  };

  const dynamicPrompts = useMemo(() => contextSnapshot.suggestedPrompts, [contextSnapshot]);

  const contextStatusLabel = workspaceQuery.isLoading
    ? "Sincronizando contexto"
    : workspaceQuery.data?.contextSnapshot?.readinessLabel || contextSnapshot.readinessLabel;

  const closeWidget = () => {
    setOpen(false);
    setMessages(initialMessages);
    setWorkspaceHydrated(false);
  };

  if (!enabled) {
    return null;
  }

  if (!open) {
    return (
      <div className="fixed bottom-5 right-5 z-40 hidden xl:block">
        <Button
          type="button"
          onClick={() => setOpen(true)}
          className="group flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-[linear-gradient(135deg,#020617_0%,#0f172a_55%,#111827_100%)] p-0 text-white shadow-xl shadow-slate-900/20 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_28px_rgba(15,23,42,0.24)] focus-visible:ring-2 focus-visible:ring-[#C0FF00] focus-visible:ring-offset-2"
          aria-label={`Abrir ${assistantTitle}`}
        >
          <span className="relative flex items-center justify-center">
            <Bot className="h-5 w-5" />
            <Sparkles className="absolute -right-1.5 -top-1.5 h-3 w-3 text-[#C0FF00]" />
          </span>
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-24 right-5 z-40 hidden w-[340px] max-w-[calc(100vw-2rem)] xl:block">
      <div className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white shadow-xl shadow-slate-900/10">
        <div className="border-b border-slate-200 bg-white px-4 py-3">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-slate-900">
                <div className="rounded-full bg-slate-100 p-2 text-slate-700">
                  <Bot className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-950">{assistantTitle}</p>
                  <p className="text-xs text-slate-500">{contextStatusLabel}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setOpen(false)}
                className="h-8 w-8 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                aria-label={`Minimizar ${assistantTitle}`}
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={closeWidget}
                className="h-8 w-8 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                aria-label={`Fechar ${assistantTitle}`}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <p className="mt-3 text-xs leading-5 text-slate-600">
            Faça uma pergunta curta sobre os dados desta tela.
          </p>
        </div>

        <div className="p-3">
          <AIChatBox
            messages={messages}
            onSendMessage={handleSendMessage}
            isLoading={workspaceQuery.isLoading || chatMutation.isPending}
            height="420px"
            placeholder={`Pergunte sobre indicadores ou prioridades de ${assistantTitle.toLowerCase()}...`}
            emptyStateMessage="Faça uma pergunta objetiva para começar"
            suggestedPrompts={dynamicPrompts}
            compact
            className="border-slate-200 shadow-none"
          />
        </div>
      </div>
    </div>
  );
}
